import { type EconomyBalanceDto, GOLD } from '@penalty-cup/shared';
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../api.js';
import { useApp } from '../store.js';
import { useBackButton } from '../telegram-back.js';

/** Обмен серебра на Gold (Economy v0.1, п. 65): 1000 : 1, не больше 1 Gold в сутки. */
export function Exchange() {
  const token = useApp((s) => s.token);
  const setView = useApp((s) => s.setView);
  const back = useCallback(() => setView(null), [setView]);
  useBackButton(back);
  const [bal, setBal] = useState<EconomyBalanceDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const settings = useApp((s) => s.settings);
  const rate = settings?.silverGoldRate ?? GOLD.silverRate;
  const perDay = settings?.goldExchangePerDay ?? GOLD.exchangePerDay;
  const champ4 = settings?.t4ChampionGold ?? GOLD.champion[4];
  const champ8 = settings?.t8ChampionGold ?? GOLD.champion[8];

  const apply = useCallback((b: EconomyBalanceDto) => {
    setBal(b);
    const me = useApp.getState().me;
    if (me) useApp.setState({ me: { ...me, coins: b.silver, gold: b.gold, tickets: b.tickets } });
  }, []);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    api.economyBalance(token).then(
      (b) => alive && apply(b),
      () => alive && setMsg('Баланс не загрузился. Проверьте связь'),
    );
    return () => {
      alive = false;
    };
  }, [token, apply]);

  const exchange = async () => {
    if (!token || busy) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.exchangeSilverGold(token, 1);
      apply(await api.economyBalance(token));
      setMsg('Готово: +1 🟡');
    } catch (err) {
      const code = err instanceof ApiError ? err.message : '';
      setMsg(
        code === 'daily_limit'
          ? 'Сегодня обмен уже был — следующий завтра'
          : code === 'not_enough_silver'
            ? 'Не хватает серебра'
            : 'Не получилось',
      );
    } finally {
      setBusy(false);
    }
  };

  const enough = !!bal && bal.silver >= rate;
  return (
    <main className="screen exchange screen--nav">
      <h1 className="screen__title">Обмен</h1>
      {bal && (
        <>
          <div className="exchange__side">
            <span>🪙 Серебро</span>
            <b>{bal.silver}</b>
          </div>
          <div className="exchange__rate" aria-label="Курс">
            ↓ {rate} : 1 ↓
          </div>
          <div className="exchange__side exchange__side--gold">
            <span>🟡 Gold</span>
            <b>{bal.gold.claimable + bal.gold.bonus}</b>
          </div>
          {bal.gold.bonus > 0 && (
            <p className="field__hint">
              из них бонусного {bal.gold.bonus} — за кубки с ботами, только для игры
            </p>
          )}
          <button
            type="button"
            className="play play--small"
            disabled={busy || !enough || bal.exchangeLeftToday === 0}
            onClick={() => void exchange()}
          >
            Обменять {rate} 🪙 на 1 🟡
          </button>
          <p className="field__hint">
            Сегодня: {perDay - bal.exchangeLeftToday} / {perDay}
            {!enough && bal.exchangeLeftToday > 0 ? ` · нужно ещё ${rate - bal.silver} 🪙` : ''}
          </p>
        </>
      )}
      {bal && (
        <button type="button" className="chip" onClick={() => setView('season')}>
          🏆 Сезон Gold{bal.gold.committed ? ` · вложено ${bal.gold.committed} 🟡` : ''}
        </button>
      )}
      {msg && (
        <p className="field__hint" role="status">
          {msg}
        </p>
      )}
      <p className="field__hint">
        Gold — редкая валюта: обмен ({perDay} в день) и кубки — на 4 (+{champ4}) и на 8 (+
        {champ8}). За кубок без ботов Gold засчитывается в сезон, с ботами — бонусный, только для игры.
      </p>
    </main>
  );
}
